import { useEffect, useState } from 'react';
import type { PlaceFilters, PlaceStatus, VisitedReturnIntent } from '../domain/types';
import { RETURN_INTENT_LABELS, STATUS_LABELS } from '../domain/labels';
import { Icon } from './Icon';

interface FiltersDialogProps {
  open: boolean;
  filters: PlaceFilters;
  availableTags: readonly string[];
  onApply: (filters: PlaceFilters) => void;
  onClose: () => void;
}

function toggleValue<T>(values: readonly T[] | undefined, value: T): T[] {
  const current = values ?? [];
  return current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
}

export function FiltersDialog({ open, filters, availableTags, onApply, onClose }: FiltersDialogProps) {
  const [draft, setDraft] = useState<PlaceFilters>(filters);

  useEffect(() => {
    if (open) setDraft(filters);
  }, [filters, open]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, open]);

  if (!open) return null;

  const statuses: PlaceStatus[] = ['want', 'visited'];
  const intents: VisitedReturnIntent[] = ['yes', 'no', 'unsure'];

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.currentTarget === event.target) onClose();
    }}>
      <section className="modal-sheet" role="dialog" aria-modal="true" aria-labelledby="filters-title">
        <header className="modal-header">
          <h2 id="filters-title">Фильтры</h2>
          <button className="icon-button icon-button--plain" type="button" aria-label="Закрыть фильтры" onClick={onClose}>
            <Icon name="close" />
          </button>
        </header>

        <div className="filter-sections">
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Статус</legend>
            <div className="checkbox-list">
              {statuses.map((status) => (
                <label className="checkbox-row" key={status}>
                  <input
                    type="checkbox"
                    checked={draft.statuses?.includes(status) ?? false}
                    onChange={() => setDraft((current) => ({ ...current, statuses: toggleValue(current.statuses, status) }))}
                  />
                  <span>{STATUS_LABELS[status]}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="fieldset">
            <legend className="fieldset-legend">После посещения</legend>
            <div className="checkbox-list">
              {intents.map((intent) => (
                <label className="checkbox-row" key={intent}>
                  <input
                    type="checkbox"
                    checked={draft.returnIntents?.includes(intent) ?? false}
                    onChange={() => setDraft((current) => ({ ...current, returnIntents: toggleValue(current.returnIntents, intent) }))}
                  />
                  <span>{RETURN_INTENT_LABELS[intent]}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {availableTags.length > 0 ? (
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Теги</legend>
              <div className="filter-tags">
                {availableTags.map((tag) => (
                  <button
                    className="filter-tag"
                    key={tag}
                    type="button"
                    aria-pressed={draft.tags?.includes(tag) ?? false}
                    onClick={() => setDraft((current) => ({ ...current, tags: toggleValue(current.tags, tag) }))}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </fieldset>
          ) : null}
        </div>

        <div className="modal-actions">
          <button className="button button--secondary" type="button" onClick={() => setDraft({})}>Сбросить всё</button>
          <button className="button" type="button" onClick={() => { onApply(draft); onClose(); }}>Показать</button>
        </div>
      </section>
    </div>
  );
}
